/**
 * ============================================
 * APP LAYOUT - Layout Principal de la Aplicación
 * ============================================
 *
 * Este componente es el "esqueleto" de toda la aplicación.
 * Ahora usa useLocation de React Router para mostrar
 * el título correcto según la página actual.
 *
 * También incluye:
 * - ThemeToggle: para cambiar entre modo claro/oscuro
 * - LanguageSelector: para cambiar el idioma (i18n)
 */

import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./app-sidebar";
import { Separator } from "@/components/ui/separator";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageSelector } from "@/components/language-selector";
import { SucursalSelector } from "@/components/sucursal-selector";
import { BusquedaAvanzadaSheet } from "@/pages/busqueda-avanzada/components/busqueda-avanzada-sheet";

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * Mapeo de rutas a claves de traducción
 * Usamos las claves del archivo de traducciones (nav.*)
 */
const routeTranslationKeys: Record<string, string> = {
  "/": "nav.dashboard",
  "/turnos": "nav.turnos",
  "/pacientes": "nav.pacientes",
  "/medicos-remitentes": "nav.medicosRemitentes",
  "/ordenes": "nav.ordenes",
  "/facturacion": "nav.facturacion",
  "/muestras": "nav.muestras",
  "/procesamiento": "nav.procesamiento",
  "/validacion": "nav.validacion",
  "/informes": "nav.informes",
  "/caja": "nav.caja",
  "/inventario": "nav.inventario",
  "/usuarios": "nav.usuarios",
  "/sucursales": "nav.sucursales",
  "/configuracion": "nav.configuracion",
};

export function AppLayout({ children }: AppLayoutProps) {
  /**
   * useLocation: Hook que nos da información de la URL actual
   * pathname: la ruta actual (ej: "/pacientes")
   */
  const location = useLocation();

  /**
   * useTranslation: Hook de i18next para obtener traducciones
   * t: función que traduce una clave al idioma actual
   */
  const { t } = useTranslation();

  // Estado del panel de búsqueda avanzada
  const [searchOpen, setSearchOpen] = useState(false);

  // Atajo de teclado Ctrl+K / Cmd+K
  const handleSearchToggle = useCallback(() => {
    setSearchOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        handleSearchToggle();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleSearchToggle]);

  // Obtener la clave de traducción según la ruta
  const translationKey = routeTranslationKeys[location.pathname] || "common.appName";
  // Traducir la clave al idioma actual
  const title = t(translationKey);

  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar onSearchClick={handleSearchToggle} />

      <SidebarInset>
        {/* Header con título dinámico */}
        <header className="sticky top-0 z-10 flex h-14 items-center gap-4 border-b bg-background px-4">
          <SidebarTrigger className="-ml-2" />
          <Separator orientation="vertical" className="h-4" />
          <h1 className="text-sm font-medium truncate flex-1">{title}</h1>

          {/* Controles del lado derecho: sucursal, idioma y tema */}
          <div className="flex items-center gap-1">
            <SucursalSelector />
            <LanguageSelector />
            <ThemeToggle />
          </div>
        </header>

        {/* Contenido principal */}
        <main className="flex-1 overflow-auto p-4">{children}</main>
      </SidebarInset>

      {/* Panel de búsqueda avanzada */}
      <BusquedaAvanzadaSheet open={searchOpen} onOpenChange={setSearchOpen} />
    </SidebarProvider>
  );
}
